import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { uniqueId } from '../form/ids';

/**
 * Misuratore "usati / limite" (FR-012, tavole 1A e 2B), per esempio "Cantieri attivi del tuo
 * account 3 / 5". È un `role="meter"` con l'etichetta e i valori in monospazio; al limite la barra
 * diventa rossa e la parola "limite raggiunto" lo dice anche senza colore.
 */
@Component({
  selector: 'bs-usage-meter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <div class="flex items-baseline justify-between gap-3 text-sm">
      <span [id]="labelId" class="text-stone-600 dark:text-stone-300">{{ label() }}</span>
      <span class="whitespace-nowrap font-mono font-medium text-ink dark:text-stone-50">
        {{ used() }} / {{ limit() }}
        @if (full()) {
          <span class="font-sans text-xs font-semibold text-red-700 dark:text-red-400"
            >· limite raggiunto</span
          >
        }
      </span>
    </div>
    <div
      role="meter"
      class="h-1.5 rounded-full bg-stone-200 dark:bg-stone-800"
      [attr.aria-labelledby]="labelId"
      aria-valuemin="0"
      [attr.aria-valuemax]="limit()"
      [attr.aria-valuenow]="used()"
      [attr.aria-valuetext]="valueText()"
      [attr.data-testid]="testId()"
    >
      <div
        class="h-1.5 rounded-full"
        [class]="full() ? 'bg-red-700 dark:bg-red-400' : 'bg-ink dark:bg-stone-50'"
        [style.width.%]="percent()"
      ></div>
    </div>
  `,
})
export class UsageMeterComponent {
  readonly label = input.required<string>();
  readonly used = input.required<number>();
  readonly limit = input.required<number>();
  readonly testId = input<string | null>(null);

  protected readonly labelId = uniqueId('bs-usage-meter');
  protected readonly full = computed(() => this.limit() > 0 && this.used() >= this.limit());
  protected readonly percent = computed(() =>
    this.limit() > 0 ? Math.min(100, Math.round((this.used() / this.limit()) * 100)) : 100,
  );
  protected readonly valueText = computed(() => `${this.used()} su ${this.limit()}`);
}
